import { Row } from "@tanstack/react-table";
import { ChevronDown } from "lucide-react";

import { getBadgeVariant } from "./ItemTable";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import * as DropDown from "@/components/ui/dropdown-menu";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import {
  ISubProductItem,
  SubProductItemStatus,
  SubProductItemStatusList,
} from "@/interfaces/tools.interface";
import { cn, formatToCapitalizedWords } from "@/lib/utils";

type TUseItemTableColumns = {
  writePermissions?: boolean;
  getActionOptions: (row: Row<ISubProductItem>) => ActionButtonDataTypes[];
  handleStatusMutation: (
    row: Row<ISubProductItem>,
    status: SubProductItemStatus,
  ) => Promise<void>;
};

export const useProductItemTableColumns = ({
  writePermissions,
  getActionOptions,
  handleStatusMutation,
}: TUseItemTableColumns): ExtendedColumnDef<ISubProductItem>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (data) => data?.name || "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.images?.[0]}
            name={`${row.original.name}`}
            title={`${row.original.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        showRightBorder: true,
        className: "max-w-[70px]",
        classNameRow: "max-w-[70px]",
      },
    },
    {
      map: {
        key: "Model Number",
        dataTransformer: (data) => data?.modelNumber || "-",
      },
      accessorKey: "Model Number",
      header: "Model Number",
      cell: ({ row }) => row.original?.modelNumber ?? "-",
      meta: {
        showRightBorder: true,
        className: "max-w-[60px]",
        classNameRow: "max-w-[60px]",
      },
    },
    {
      map: {
        key: "Status",
        dataTransformer: (data) =>
          formatToCapitalizedWords(data?.status || "-"),
      },
      accessorKey: "Status",
      header: "Status",
      cell: ({ row }): JSX.Element => {
        return (
          <DropDown.DropdownMenu>
            <DropDown.DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              disabled={!SubProductItemStatusList.length}
            >
              <Badge variant={getBadgeVariant(row.original?.status)}>
                {formatToCapitalizedWords(row.original?.status || "-").replace(
                  /_/g,
                  " ",
                )}
                <ChevronDown className='ml-1 h-4 w-4' />
              </Badge>
            </DropDown.DropdownMenuTrigger>
            <DropDown.DropdownMenuContent>
              {SubProductItemStatusList?.map((item, idx) => (
                <DropDown.DropdownMenuItem
                  key={`${item}-${idx}`}
                  className='p-3 cursor-pointer'
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleStatusMutation(
                      row,
                      item as SubProductItemStatus,
                    );
                  }}
                >
                  {formatToCapitalizedWords(item || "-").replace(/_/g, " ")}
                </DropDown.DropdownMenuItem>
              ))}
              {/* ))} */}
            </DropDown.DropdownMenuContent>
          </DropDown.DropdownMenu>
        );
      },
      meta: {
        showRightBorder: true,
        className: "max-w-[50px]",
        classNameRow: "max-w-[50px]",
      },
    },
    {
      map: {
        key: "Site Assigned",
        dataTransformer: (data) =>
          data?.SpecificSubProductAssignment?.project?.address?.label || "-",
      },
      accessorKey: "Site Assigned",
      header: "Site Assigned",
      cell: ({ row }) =>
        row.original?.SpecificSubProductAssignment?.project?.address?.label ||
        "-",
    },
    {
      id: "actions",
      hideColumn: !writePermissions,
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-20",
      },
    },
  ];
};
